import { ACTIVITY_LATE_TICK_MS, ACTIVITY_TICK_MS, type ActivitySurface, type ActivityTickItem } from './activity.model'

/** Counters that are inputs (set `lastInputT`); pointerPx/requests/waitMs/longTaskMs are not. */
export type InputCounter =
  | 'wheelZooms' | 'dragPans' | 'keyPans' | 'spacePans' | 'mapClicks'
  | 'toolbarClicks' | 'panelClicks' | 'shortcutKeys' | 'typingKeys'

export interface TickContext { surface: ActivitySurface; taskId: string | null }

const MAX_COUNTER = 32767
const MIN_WINDOW_MS = 250
const clamp = (v: number, max: number) => Math.max(0, Math.min(max, Math.round(v)))

interface Carry { requests: number; waitMs: number; longTaskMs: number }

/**
 * One 15-second window of tick counters. Pure: the caller owns the timers and
 * passes the clock readings in. Completions that happen while no window is open
 * (hidden tab, between screens) are carried into the next window opened.
 */
export class TickAccumulator {
  private open = false
  private t0 = 0
  private ctx: TickContext = { surface: 'app', taskId: null }
  private visible = true
  private counters = TickAccumulator.zero()
  private carry: Carry = { requests: 0, waitMs: 0, longTaskMs: 0 }
  private inputSinceOpen = false
  /** `t` of the last input; the session start before any input. */
  private lastInputT = 0

  private static zero() {
    return {
      wheelZooms: 0, dragPans: 0, keyPans: 0, spacePans: 0, mapClicks: 0, toolbarClicks: 0, panelClicks: 0,
      shortcutKeys: 0, typingKeys: 0, pointerPx: 0, requests: 0, waitMs: 0, longTaskMs: 0,
    }
  }

  get isOpen(): boolean { return this.open }
  get context(): TickContext { return this.ctx }

  /** New session: input clock restarts, carried completions are zeroed (D-rollover). */
  resetSession(t: number): void {
    this.lastInputT = t
    this.carry = { requests: 0, waitMs: 0, longTaskMs: 0 }
  }

  /** Opens a window (no-op when one is open); the carry moves into it. */
  openWindow(t: number, ctx: TickContext, visible: boolean): void {
    if (this.open) return
    this.open = true
    this.t0 = t
    this.ctx = ctx
    this.visible = visible
    this.counters = TickAccumulator.zero()
    this.inputSinceOpen = false
    this.counters.requests = this.carry.requests
    this.counters.waitMs = this.carry.waitMs
    this.counters.longTaskMs = this.carry.longTaskMs
    this.carry = { requests: 0, waitMs: 0, longTaskMs: 0 }
  }

  /** Counts an input; `counter` is null for an unregistered key (refreshes `msSinceInput` only). */
  input(t: number, counter: InputCounter | null): void {
    this.lastInputT = t
    if (counter && this.open) { this.counters[counter]++; this.inputSinceOpen = true }
  }

  addPointerPx(px: number): void { if (this.open) this.counters.pointerPx += px }

  /** A completed API request (or long task): into the open window, else carried. */
  addCompletion(c: Partial<Carry>): void {
    const target = this.open ? this.counters : this.carry
    target.requests += c.requests ?? 0
    target.waitMs += c.waitMs ?? 0
    target.longTaskMs += c.longTaskMs ?? 0
  }

  /** Closes the window. Returns the tick, or null when closed/too short to matter. */
  close(t: number, focused: boolean): ActivityTickItem | null {
    if (!this.open) return null
    this.open = false
    const length = t - this.t0
    const c = this.counters
    const anySet = this.inputSinceOpen || c.pointerPx > 0 || c.requests > 0 || c.waitMs > 0 || c.longTaskMs > 0
    if (length < MIN_WINDOW_MS && !anySet) return null
    const windowMs = length > ACTIVITY_LATE_TICK_MS ? ACTIVITY_TICK_MS : Math.max(1, Math.round(length))
    return {
      t: this.t0,
      windowMs,
      surface: this.ctx.surface,
      taskId: this.ctx.taskId,
      visible: this.visible,
      focused,
      msSinceInput: clamp(t - this.lastInputT, 2 ** 31 - 1),
      wheelZooms: clamp(c.wheelZooms, MAX_COUNTER),
      dragPans: clamp(c.dragPans, MAX_COUNTER),
      keyPans: clamp(c.keyPans, MAX_COUNTER),
      spacePans: clamp(c.spacePans, MAX_COUNTER),
      mapClicks: clamp(c.mapClicks, MAX_COUNTER),
      toolbarClicks: clamp(c.toolbarClicks, MAX_COUNTER),
      panelClicks: clamp(c.panelClicks, MAX_COUNTER),
      shortcutKeys: clamp(c.shortcutKeys, MAX_COUNTER),
      typingKeys: clamp(c.typingKeys, MAX_COUNTER),
      pointerPx: clamp(c.pointerPx, 10_000_000),
      requests: clamp(c.requests, MAX_COUNTER),
      waitMs: clamp(c.waitMs, 3_600_000),
      longTaskMs: clamp(c.longTaskMs, 60_000),
    }
  }
}
